import { useQuery } from '@tanstack/react-query';
import { ApiService } from '@/_services/api.service';
import { queryKeys } from './queryKeys';
import type { IMessageDetails } from '@/types';

/**
 * Query hook for lazy-loading message details (agentActivities, dataPoolEntries, dbCitations, tokenUsage).
 *
 * @param messageId - The message ID to load details for
 * @param options.enabled - Whether to enable the query (default: false for manual triggering)
 *
 * Usage:
 * - Initialize with enabled: false
 * - Call refetch() when user clicks on Thinking/Provenance tabs
 * - Data is cached indefinitely (staleTime: Infinity)
 */
export const useMessageDetailsQuery = (messageId: string | undefined, options?: { enabled?: boolean }) => {
	return useQuery({
		queryKey: queryKeys.messages.details(messageId),
		queryFn: async (): Promise<IMessageDetails | null> => {
			if (!messageId) return null;

			const response = await ApiService.GetMessageDetails(messageId);

			if (response.error) {
				throw new Error(response.error.message);
			}

			return response.data;
		},
		enabled: !!messageId && (options?.enabled ?? false),
		staleTime: Infinity, // Never refetch - message details don't change
		gcTime: 30 * 60 * 1000 // Keep in cache for 30 minutes
	});
};
